import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { useResolveBdFindings } from '../../../hooks/useBasicDesign.js';
import { severityTone } from './bd-review.js';
import { BdApiErrorText } from './BdApiErrorText.js';
import type { BdFindingAction, BdReview, BdReviewFinding } from '../../../lib/api/basic-design.js';

export interface BdFindingsListProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** レビュー */
  review: BdReview;
}

/** 未解決の指摘に出す操作。 @graph-connects none */
const OPEN_ACTIONS: readonly BdFindingAction[] = ['fixed', 'dismissed', 'withdrawn'];

/**
 * レビューの指摘の一覧。観点・重さ・指す箇所・見つけた回と、作者の解決の操作（直した・見送る・
 * 取り消す、解決済みは未解決に戻す）。見送る・取り消すの理由が要るかは API が判定する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの指摘を観点・重さ・箇所つきで並べ、作者が直した・見送る・取り消す・未解決に戻す
 * @graph-connects useResolveBdFindings [calls] 指摘の解決
 */
export function BdFindingsList({ bdKey, review }: BdFindingsListProps) {
  const { t } = useTranslation();
  const resolve = useResolveBdFindings(bdKey);
  const open = review.findings.filter((f) => f.resolution === 'open').length;

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-[var(--color-text-primary)]">
        {t('bd.review.findings', { open, total: review.findings.length })}
      </h3>
      {review.findings.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{t('bd.review.noFindings')}</p>
      ) : (
        <ul aria-label={t('bd.review.findingsAria')} className="space-y-2">
          {review.findings.map((finding) => (
            <li key={finding.id}>
              <FindingItem
                finding={finding}
                perspective={
                  review.perspectives.find((p) => p.key === finding.perspective)?.label ??
                  finding.perspective
                }
                busy={resolve.isPending}
                onAction={(action, note) => {
                  resolve.mutate([
                    {
                      id: finding.id,
                      action,
                      ...(note.trim() === '' ? {} : { note: note.trim() }),
                    },
                  ]);
                }}
              />
            </li>
          ))}
        </ul>
      )}
      {resolve.error === null ? null : <BdApiErrorText error={resolve.error} />}
    </div>
  );
}

interface FindingItemProps {
  finding: BdReviewFinding;
  /** 観点の表示名 */
  perspective: string;
  busy: boolean;
  onAction: (action: BdFindingAction, note: string) => void;
}

/** 指摘 1 件。 @graph-connects none */
function FindingItem({ finding, perspective, busy, onAction }: FindingItemProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const isOpen = finding.resolution === 'open';

  return (
    <div className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <code className="text-[var(--color-text-muted)]">{finding.id}</code>
        <Badge size="sm" variant={severityTone(finding.severity)}>
          {t(`bd.review.severity.${finding.severity}`)}
        </Badge>
        <span className="font-medium text-[var(--color-text-primary)]">{perspective}</span>
        {finding.target === '' ? null : (
          <code className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5">
            {finding.target}
          </code>
        )}
        <span className="text-[var(--color-text-muted)]">
          {t('bd.review.foundBy', {
            by: t(`bd.review.by.${finding.by}`),
            round: finding.foundInRound,
          })}
        </span>
        <Badge size="sm" variant={isOpen ? 'warning' : 'default'}>
          {t(`bd.review.resolution.${finding.resolution}`)}
        </Badge>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-[var(--color-text-primary)]">
        {finding.summary}
      </p>
      {finding.resolutionNote === '' ? null : (
        <p className="mt-1 text-[var(--color-text-secondary)]">
          {t('bd.review.note', { note: finding.resolutionNote })}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-end gap-2">
        {isOpen ? (
          <>
            <div className="min-w-48 flex-1">
              <Input
                size="sm"
                fullWidth
                aria-label={t('bd.review.noteAria', { id: finding.id })}
                placeholder={t('bd.review.notePlaceholder')}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                }}
              />
            </div>
            {OPEN_ACTIONS.map((action) => (
              <Button
                key={action}
                size="sm"
                variant="secondary"
                disabled={busy}
                aria-label={t(`bd.review.actionAria.${action}`, { id: finding.id })}
                onClick={() => {
                  onAction(action, note);
                }}
              >
                {t(`bd.review.action.${action}`)}
              </Button>
            ))}
          </>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            aria-label={t('bd.review.actionAria.reopen', { id: finding.id })}
            onClick={() => {
              onAction('reopen', '');
            }}
          >
            {t('bd.review.action.reopen')}
          </Button>
        )}
      </div>
    </div>
  );
}
